// components/climate/wildfire/WildfireFwiDays.tsx
import { Alert, Box, CircularProgress } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useMemo } from 'react'
import { EmissionTier, MetricState, AdaptationView } from '../../common'
import { usePropertyClimateData, isActiveMetricState } from '../../../../hooks/climateData'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import MetricStatePlaceholder from '../../common/MetricStatePlaceholder'

const WF_FWI_DAYS_METRIC_KEY = 'wf:days_above_fwi_threshold'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

type FwiPoint = { x: number; y: number }

export default function WildfireFwiDays({
  propertyId,
  emissionTier,
  view,
}: {
  propertyId: number
  emissionTier: EmissionTier
  view?: AdaptationView
}) {
  const queryParams = useMemo(() => ({ view }), [view])
  const {
    data: fwiData,
    isLoading,
    error,
  } = usePropertyClimateData(WF_FWI_DAYS_METRIC_KEY, { propertyId }, queryParams)

  const renderBody = () => {
    if (isLoading && !fwiData) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
          <CircularProgress size={24} />
        </Box>
      )
    }

    if (error) {
      return <Alert severity="error">Could not load fire weather index data.</Alert>
    }

    if (!fwiData) {
      return <Alert severity="info">No fire weather index data available for this property.</Alert>
    }

    // Fire weather conditions are a climate input; they are not recomputed by impact
    // models, so show neutral loading/empty states rather than the adaptation
    // "calculating" indicator.
    if (fwiData.state.value !== MetricState.SUCCEEDED) {
      return isActiveMetricState(fwiData.state.value) ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <MetricStatePlaceholder state={fwiData.state.value} minHeight={160} />
      )
    }

    const tierData = fwiData.data.data?.[emissionTier] as Record<string, FwiPoint[]> | undefined

    if (!tierData) {
      return (
        <Alert severity="info">
          No FWI data for emission tier <strong>{emissionTier}</strong>.
        </Alert>
      )
    }

    // Thresholds from keys or derived
    let thresholds: string[] = (fwiData.data.keys.threshold as string[] | undefined) || Object.keys(tierData)

    if (!thresholds.length) {
      return <Alert severity="info">No FWI thresholds found in data.</Alert>
    }

    // Sort thresholds numerically: 15, 30, 45
    thresholds = thresholds.sort((a, b) => parseFloat(a) - parseFloat(b))

    const firstThresholdWithData = thresholds.find(th => (tierData[th] || []).length)
    const baseSeries = firstThresholdWithData ? tierData[firstThresholdWithData] || [] : []

    const labels = baseSeries.map(p => String(p.x))

    const colors = ['#1976d2', '#ff9800', '#d32f2f']

    const chartData: ChartData<'line'> = {
      labels,
      datasets: thresholds.map((th, idx) => {
        const series = tierData[th] || []
        return {
          label: `FWI ≥ ${th}`,
          data: series.map(p => p.y),
          borderColor: colors[idx % colors.length],
          backgroundColor: colors[idx % colors.length],
          tension: 0.2,
          pointRadius: 3,
        }
      }),
    }

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
        },
        tooltip: {
          callbacks: {
            label: ctx => {
              const label = ctx.dataset.label || ''
              const value = ctx.parsed.y as number
              return `${label}: ${value.toFixed(2)} days / year`
            },
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year',
          },
        },
        y: {
          title: {
            display: true,
            text: 'Days per year above threshold',
          },
          beginAtZero: true,
        },
      },
    }

    const hasData = labels.length && chartData.datasets.some(ds => ds.data.length)

    if (!hasData) {
      return <Alert severity="info">No FWI days data to display for this property and emission tier.</Alert>
    }

    return (
      <Box sx={{ height: 260 }}>
        <Line data={chartData} options={options} />
      </Box>
    )
  }

  return (
    <Box>
      <Box mb={1}>
        <ClimateSectionTitle
          title="Fire Weather Conditions"
          subtitle="Days per year when the Fire Weather Index exceeds common thresholds"
          helpText="This chart shows the full available projection series for the selected emissions scenario. It is not filtered by the header horizon."
        />
      </Box>
      {renderBody()}
    </Box>
  )
}
